import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { fmtWIB } from "@/lib/dateFmt";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function VersionsPanel({ instrumentId }) {
  const { lang } = useLang();
  const id = lang === "id";
  const [versions, setVersions] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get(`/admin/instruments/${instrumentId}/versions`).then(r => setVersions(r.data)); }, [instrumentId]);

  const view = async (v) => {
    const r = await api.get(`/admin/instrument-versions/${v.id}`);
    setOpen(r.data);
  };

  return (
    <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
      <div>
        <div className="font-display text-lg">{id ? "Riwayat Versi" : "Version history"}</div>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          {id
            ? "Versi baru tercatat otomatis setiap kali soal, dimensi, atau skoring tes yang sudah published berubah. Perubahan deskripsi, cover, atau pengaturan share tidak membuat versi baru. Setiap respons menyimpan versi yang dipakai saat diisi."
            : "A new version is recorded automatically whenever the questions, dimensions or scoring of a published test change. Changes to descriptions, covers or share settings do not create a version. Each response stores the version it was taken on."}
        </p>
      </div>
      {versions.length === 0 ? (
        <div className="text-sm text-muted-foreground">{id ? "Belum ada versi. Versi pertama tercatat saat tes dipublikasikan." : "No versions yet. The first one is recorded when the test is published."}</div>
      ) : (
        <div className="divide-y divide-border">
          {versions.map(v => (
            <div key={v.id} className="py-3 flex items-center justify-between gap-4 text-sm" data-testid={`version-row-${v.version_number}`}>
              <div className="min-w-0">
                <div className="font-display flex items-center gap-2">
                  v{v.version_number}
                  {v.is_current && <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{id ? "Aktif" : "Current"}</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fmtWIB(v.created_at, lang)} · {v.created_by_email || "system"} · {v.question_count} {id ? "soal" : "questions"} · {v.response_count} {id ? "respons" : "responses"}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => view(v)} data-testid={`version-view-${v.version_number}`}>{id ? "Lihat" : "View"}</Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="version-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">v{open?.version_number}</DialogTitle>
          </DialogHeader>
          {open && <VersionContent snap={open.snapshot || {}} lang={lang} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VersionContent({ snap, lang }) {
  const id = lang === "id";
  const pick = (o, k) => (o?.[`${k}_${lang}`] || o?.[`${k}_id`] || "");
  const dimCode = Object.fromEntries((snap.dimensions || []).map(d => [d.id, d.code]));
  return (
    <div className="space-y-5 text-sm">
      <div>
        <div className="text-xs uppercase text-muted-foreground mb-1">{id ? "Dimensi" : "Dimensions"}</div>
        <div className="flex flex-wrap gap-1.5">
          {(snap.dimensions || []).map(d => <Badge key={d.id} variant="outline">{d.code} · {pick(d, "name")}</Badge>)}
        </div>
      </div>
      <div>
        <div className="text-xs uppercase text-muted-foreground mb-2">{id ? "Soal" : "Questions"} ({(snap.questions || []).length})</div>
        <ol className="space-y-3">
          {(snap.questions || []).map(q => (
            <li key={q.id} className="p-3 rounded-lg border border-border">
              <div className="flex gap-2">
                <span className="font-mono text-xs text-muted-foreground shrink-0">{q.code}</span>
                <span className="flex-1">{pick(q, "text")}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {(q.dimension_ids || []).map(x => dimCode[x]).filter(Boolean).join(", ")}{q.reverse_scored ? " · Rev" : ""}
                </span>
              </div>
              {(q.options || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {q.options.map(o => <span key={o.id} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary/60">{pick(o, "label")} ({o.score})</span>)}
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
